import Closable from "../behaviors/closable";

import { FiChevronDown } from "react-icons/fi";
import clsx from "clsx";
import { isMobile } from "utils/browser";

import { useState, useEffect, useMemo, useRef } from "react";

interface Option {
  label: string;
  value: any;
}

interface SelectorProps {
  label: string;
  value: any;
  name: string;
  options: Option[] | string[] | any[];
  onSelect: ({ name: string, value: any }) => void;
  disabled?: boolean;
  className?: string;
  onClickDisabled?: () => void;
  callback?: (value: any) => void;
}

export default function Select(props: SelectorProps): JSX.Element {
  const { label, value, name, options } = props;
  const { disabled } = props;
  const { onClickDisabled, callback } = props;
  const { className } = props;

  const [activeOption, setActiveOption] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState(selectedLabelFn());
  const [query, setQuery] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const selectWrapper = useRef(null);
  const optionsWrapper = useRef(null);

  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        let label;
        if (typeof option == "object" && "label" in option) {
          label = option.label;
        } else {
          label = option;
        }

        const value = normalizeString(query);
        return normalizeString(`${label}`).indexOf(value) !== -1;
      }),
    [options, query]
  );

  useEffect(() => callback && callback(value), [value]);
  useEffect(() => setActiveOption(0), [filteredOptions]);
  useEffect(() => setSelectedLabel(selectedLabelFn()), [options, value]);
  useEffect(() => showOptions && calculateHeight(), [showOptions]);

  function handleSelect(option) {
    props.onSelect({ name, value: optionValue(option) });
  }

  function handleQueryChange(newQuery) {
    setQuery(newQuery);
  }

  function handleQueryAction(key) {
    switch (key) {
      case "ArrowDown": {
        const newActive =
          activeOption === filteredOptions.length - 1
            ? filteredOptions.length - 1
            : activeOption + 1;
        return setActiveOption(newActive);
      }
      case "ArrowUp": {
        return setActiveOption(activeOption === 0 ? 0 : activeOption - 1);
      }
      case "Enter": {
        handleSelect(filteredOptions[activeOption]);
        return setShowOptions(false);
      }
    }
  }

  function handleClick(event) {
    if (event.target.tagName === "INPUT") return;
    if (disabled) return onClickDisabled && onClickDisabled();

    setShowOptions((prevValue) => !prevValue);
  }

  function optionValue(option) {
    return typeof option === "object" ? option.value : option;
  }

  function selectedLabelFn() {
    const option = options.find((option) => {
      return JSON.stringify(optionValue(option)) == JSON.stringify(value);
    });
    return option?.label || option;
  }

  function normalizeString(string) {
    return string
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  function calculateHeight() {
    if (isMobile()) {
      optionsWrapper.current.classList.remove("invisible");
      return setTimeout(() => selectWrapper.current?.scrollIntoView(), 100);
    }

    const spaceFromBottom = 10;
    const distanceFromBottom =
      window.innerHeight -
      selectWrapper.current.getBoundingClientRect().top -
      selectWrapper.current.offsetHeight;

    const optionsHeight = optionsWrapper.current.offsetHeight;
    const overflow = optionsHeight + spaceFromBottom - distanceFromBottom;
    if (overflow < 0)
      return optionsWrapper.current.classList.remove("invisible");

    if (overflow > optionsHeight / 2) {
      optionsWrapper.current.style.top = "auto";
      optionsWrapper.current.style.bottom = "100%";
    } else {
      const newMaxHeight = Math.round(optionsHeight - overflow - 20);
      optionsWrapper.current.style.maxHeight = `${newMaxHeight}px`;
    }
    optionsWrapper.current.classList.remove("invisible");
  }

  return (
    <div
      className={clsx("select-wrapper relative transition-opacity", {
        disabled
      })}
      onClick={handleClick}
      ref={selectWrapper}
    >
      <div
        className={clsx(
          "flex items-center justify-between p-4 w-full bg-gray-100 rounded-md cursor-pointer",
          className
        )}
      >
        <div className="flex flex-col">
          <LabelOrInput
            label={label}
            selectedLabel={selectedLabel}
            showOptions={showOptions}
            onChange={handleQueryChange}
            onAction={handleQueryAction}
          />
          {selectedLabel && !showOptions && (
            <span
              className={clsx("value text-emphasis-medium", {
                "mt-1": !!label
              })}
            >
              {selectedLabel}
            </span>
          )}
        </div>
        <div className="text-emphasis-low">
          <FiChevronDown size={20} />
        </div>
      </div>
      {showOptions && (
        <Closable onClose={() => setShowOptions(false)}>
          <ul
            ref={optionsWrapper}
            className="select-options max-h-[300px] absolute z-40 top-full w-full rounded-b-md shadow-lg invisible overflow-hidden overflow-y-scroll"
          >
            {filteredOptions.map((option, index) => {
              return (
                <li
                  key={JSON.stringify(optionValue(option))}
                  className={clsx(
                    "p-4 w-full hover:bg-gray-50 bg-white cursor-pointer",
                    { "bg-gray-100 active": activeOption === index }
                  )}
                  onClick={() => handleSelect(option)}
                >
                  {option.label || option}
                </li>
              );
            })}
          </ul>
        </Closable>
      )}
    </div>
  );
}

function LabelOrInput(props) {
  const { selectedLabel, label, showOptions } = props;
  const { onChange, onAction } = props;

  const [query, setQuery] = useState("");

  useEffect(() => {
    if (showOptions) return;
    setQuery("");
    onChange("");
  }, [showOptions]);

  function handleChange(event) {
    setQuery(event.target.value);
    onChange(event.target.value);
  }

  function handleKeyDown(event) {
    if (!["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) return;

    onAction(event.key);
  }

  if (!showOptions)
    return (
      <span
        className={`label font-semibold text-emphasis-medium ${
          selectedLabel ? "text-sm" : ""
        }`}
      >
        {label}
      </span>
    );

  return (
    <input
      className="mb-0 p-0 bg-gray-50 bg-opacity-0 border-none outline-none"
      autoFocus
      type="text"
      value={query}
      placeholder={label}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
}
